"use client";

import type { ReactNode } from "react";
import type { LeadFlowAnswerStore, LeadFlowAnswers } from "../types";

import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createAnswerStore } from "../utils/create-answer-store";
import { SessionUrlWatcher } from "./SessionUrlWatcher";

const LeadFlowAnswerStoreContext = createContext<LeadFlowAnswerStore | null>(
  null,
);

type LeadFlowAnswerStoreProviderProps = {
  children: ReactNode;
};

function LeadFlowAnswerStoreProvider({
  children,
}: LeadFlowAnswerStoreProviderProps) {
  const [store] = useState(() => createAnswerStore());

  useEffect(() => () => store.dispose(), [store]);

  return (
    <LeadFlowAnswerStoreContext.Provider value={store}>
      <Suspense fallback={null}>
        <SessionUrlWatcher store={store} />
      </Suspense>
      {children}
    </LeadFlowAnswerStoreContext.Provider>
  );
}

function ignoreChange() {
  // A snapshot never changes.
}

function createAnswerSnapshot(answers: LeadFlowAnswers): LeadFlowAnswerStore {
  return {
    clear: ignoreChange,
    dispose: ignoreChange,
    getServerSnapshot: () => answers,
    getSnapshot: () => answers,
    hasLoadedAnswers: () => true,
    resetFor: ignoreChange,
    setAnswer: ignoreChange,
    subscribe: () => ignoreChange,
    subscribeToClear: () => ignoreChange,
  };
}

type LeadFlowAnswerSnapshotProviderProps = {
  answers: LeadFlowAnswers;
  children: ReactNode;
};

/**
 * Gives its children a read-only copy of answers the page store no longer
 * holds, such as the thank-you screen after submit clears the store.
 */
function LeadFlowAnswerSnapshotProvider({
  answers,
  children,
}: LeadFlowAnswerSnapshotProviderProps) {
  const snapshot = useMemo(() => createAnswerSnapshot(answers), [answers]);

  return (
    <LeadFlowAnswerStoreContext.Provider value={snapshot}>
      {children}
    </LeadFlowAnswerStoreContext.Provider>
  );
}

function useLeadFlowAnswerStore(): LeadFlowAnswerStore {
  const store = useContext(LeadFlowAnswerStoreContext);
  if (!store) {
    throw new Error(
      "useLeadFlowAnswerStore must be used within LeadFlowAnswerStoreProvider",
    );
  }
  return store;
}

export {
  LeadFlowAnswerSnapshotProvider,
  LeadFlowAnswerStoreProvider,
  useLeadFlowAnswerStore,
};
